/** One screen for Goods Receipt, Issue and Stock Transfer.
 *
 * `stores.ts` declares the three; this draws them. Ledger **D28** applied to
 * our own code: GHERP wrote nine receipt forms and the ninth always missed
 * the fix, and three near-identical React screens would be the same mistake
 * at a smaller scale.
 *
 * TWO PHASES, AND THE SECOND IS A PERMISSION:
 *
 *     Draft ──authorise──> Authorised   (movements posted to the ledger)
 *       └────── cancel ──> Cancelled     (nothing posted)
 *
 * Recording what arrived and posting it are different acts. A posting changes
 * the company's stock value and its weighted average — so `authorise` is its
 * own right, and the screen says what will happen before it asks.
 *
 * AVAILABILITY IS SHOWN WHILE TYPING, not held back until Save. As soon as an
 * item, a location and a stage are known, the line says what is on the shelf.
 * `erp_post_movement` still refuses an over-issue and always will, because a
 * check in a screen is advice — this is the courtesy in front of the control.
 * `UI_TASTE.md`, and the job work screen already works this way.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { SearchSelect } from '@/components/SearchSelect'
import type { StoresDoc, DocField } from './stores'
import { today } from './stores'
import { useObjectRights } from '@/lib/rights'

type Row = Record<string, unknown>
interface Opt { value: string; label: string }

const asDate = (v: unknown) => {
  const s = String(v ?? '')
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || '—')
}
const num = (v: unknown, dp = 3) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString('en-IN',
        { minimumFractionDigits: dp, maximumFractionDigits: dp })

export function StoresDocScreen({ spec }: { spec: StoresDoc }) {
  const [rows, setRows] = useState<Row[]>([])
  const [lookups, setLookups] = useState<Record<string, Opt[]>>({})
  const [valuesVisible, setValuesVisible] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const [head, setHead] = useState<Row | null>(null)
  // stores.<object>.add / authorise / cancel — the checks stores/index.py
  // makes. For deciding what to SHOW; the server decides. RIGHTS_MODEL 6.1.
  const rights = useObjectRights('stores', spec.object)
  const [lines, setLines] = useState<Row[]>([])
  /** line index -> what the ledger says is on the shelf */
  const [avail, setAvail] = useState<Record<number, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<{ rows: Row[]; values_visible: boolean }>(
        `/stores/${spec.kind}`,
        { q: search || undefined, status: statusFilter || undefined })
      setRows(r.rows ?? [])
      setValuesVisible(r.values_visible)
      setProblem(null)
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message
                                       : 'The list could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [spec.kind, search, statusFilter])

  useEffect(() => { void load() }, [load])

  // Every lookup this document needs, fetched once.
  useEffect(() => {
    const fields = [...spec.header, ...spec.lines].filter(f => f.from)
    const seen = new Map<string, DocField>()
    for (const f of fields) if (!seen.has(f.from!.path)) seen.set(f.from!.path, f)

    Promise.all([...seen.values()].map(async f => {
      const r = await api.get<{ rows: Row[] }>(`/${f.from!.path}`)
      return [f.from!.path, (r.rows ?? []).map(x => ({
        value: String(x[f.from!.value] ?? ''),
        label: f.from!.value === 'item_id'
          ? `${x[f.from!.value]} — ${x[f.from!.label]}`
          : String(x[f.from!.label] ?? ''),
      }))] as const
    })).then(pairs => setLookups(Object.fromEntries(pairs)))
      .catch(() => { /* a lookup that cannot be read shows as empty, and the
                        SearchSelect says so rather than looking finished */ })
  }, [spec])

  /** Ask the ledger what is there, as soon as the line names enough to ask. */
  const checkAvailability = useCallback(async (i: number, line: Row, h: Row) => {
    const a = spec.availability
    if (!a) return
    const item = line.item_id, loc = h[a.locationField], stage = line[a.stageField]
    if (!item || !loc || !stage) {
      setAvail(p => ({ ...p, [i]: '' }))
      return
    }
    try {
      const r = await api.get<{ qty: number }>('/stores/stock/position', {
        item_id: String(item), location_id: String(loc), stage: String(stage),
      })
      setAvail(p => ({ ...p, [i]: `${num(r.qty)} on hand` }))
    } catch {
      setAvail(p => ({ ...p, [i]: 'could not be checked' }))
    }
  }, [spec])

  function startNew() {
    const h: Row = { doc_date: today() }
    for (const f of spec.header) {
      if (f.type === 'select' && f.required) h[f.key] = f.options?.[0]?.value ?? ''
    }
    setHead(h)
    setLines([{ stage: 'STOCK', from_stage: 'STOCK', to_stage: 'STOCK' }])
    setAvail({})
  }

  async function save() {
    if (!head) return
    setBusy('new')
    try {
      await api.create(`/stores/${spec.kind}`, { ...head, lines })
      setHead(null); setLines([])
      await load()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'It could not be saved.')
    } finally { setBusy(null) }
  }

  async function act(r: Row, what: 'authorise' | 'cancel') {
    const id = String(r[spec.idKey])
    if (what === 'authorise' && !window.confirm(
      `Authorise this ${spec.singular.toLowerCase()}?\n\n${spec.posts}\n\n`
      + 'Once authorised it cannot be changed — it is reversed by another '
      + 'document.')) return

    setBusy(id)
    try {
      await api.create(`/stores/${spec.kind}/${id}/${what}`,
                       { version: r.version })
      await load()
    } catch (e) {
      setProblem(e instanceof Error ? e.message
                                    : 'That could not be completed.')
    } finally { setBusy(null) }
  }

  function field(f: DocField, value: unknown, onChange: (v: string) => void) {
    if (f.value && !valuesVisible) {
      return <span className="masked">••••</span>
    }
    if (f.from) {
      return (
        <SearchSelect
          value={String(value ?? '')}
          emptyLabel={`— ${f.label.toLowerCase()} —`}
          options={lookups[f.from.path] ?? []}
          onChange={onChange}
        />
      )
    }
    if (f.type === 'select') {
      return (
        <SearchSelect
          value={String(value ?? '')}
          emptyLabel={`— ${f.label.toLowerCase()} —`}
          options={f.options ?? []}
          onChange={onChange}
        />
      )
    }
    return (
      <input
        type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
        value={String(value ?? '')}
        onChange={e => onChange(e.target.value)}
      />
    )
  }

  return (
    <div className="screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">{spec.title}</h1>
          <p className="page-sub">
            {loading ? 'Loading…' : `${rows.length} document${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      {problem && <div className="form-errors">{problem}</div>}

      {head && (
        <div className="card">
          <div className="card-head"><h2>New {spec.singular}</h2></div>

          <div className="form-grid">
            {spec.header.map(f => (
              <div className="form-field" key={f.key}>
                <label>
                  {f.label}
                  {f.required && <span className="req" aria-hidden="true"> *</span>}
                </label>
                {field(f, head[f.key], v => {
                  const h = { ...head, [f.key]: v }
                  setHead(h)
                  lines.forEach((ln, i) => void checkAvailability(i, ln, h))
                })}
                {f.help && <div className="form-help">{f.help}</div>}
              </div>
            ))}
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  {spec.lines.map(f => (
                    <th key={f.key} style={f.width ? { width: f.width } : undefined}>
                      {f.label}
                      {f.required && <span className="req"> *</span>}
                    </th>
                  ))}
                  {spec.availability && <th>On Hand</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((ln, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    {spec.lines.map(f => (
                      <td key={f.key}>
                        {field(f, ln[f.key], v => {
                          const next = lines.map((l, j) =>
                            j === i ? { ...l, [f.key]: v } : l)
                          setLines(next)
                          void checkAvailability(i, next[i], head)
                        })}
                      </td>
                    ))}
                    {spec.availability && (
                      // Told while typing, not refused afterwards.
                      <td className="hint">{avail[i] ?? ''}</td>
                    )}
                    <td>
                      {lines.length > 1 && (
                        <button className="link"
                                onClick={() => setLines(
                                  lines.filter((_, j) => j !== i))}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-actions">
            <button className="btn btn-secondary"
                    onClick={() => setLines([...lines,
                      { stage: 'STOCK', from_stage: 'STOCK', to_stage: 'STOCK' }])}>
              Add Line
            </button>
            <div className="grow" />
            <button className="btn btn-primary" disabled={busy === 'new'}
                    onClick={() => void save()}>
              {busy === 'new' ? 'Saving…' : 'Save as Draft'}
            </button>
            <button className="btn btn-secondary" disabled={busy === 'new'}
                    onClick={() => { setHead(null); setLines([]) }}>
              Cancel
            </button>
          </div>

          <p className="form-note">
            Saved as a <strong>Draft</strong>. Nothing reaches the stock ledger
            until it is authorised.
          </p>
        </div>
      )}

      <div className="card">
        <div className="card-head toolbar">
          <input className="search" placeholder="Search by document number…"
                 value={search} onChange={e => setSearch(e.target.value)} />
          <select value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="Draft">Draft</option>
            <option value="Authorised">Authorised</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          {rights.can('add') && (
            <button className="btn btn-primary" onClick={startNew}>
              New {spec.singular}
            </button>
          )}
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Document</th><th>Date</th><th>Lines</th><th>Status</th>
                {valuesVisible && <th className="num">Value</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={valuesVisible ? 6 : 5} className="empty-cell">
                  {search || statusFilter
                    ? 'Nothing matches that.'
                    : `No ${spec.title.toLowerCase()} yet.`}
                </td></tr>
              )}
              {rows.map(r => {
                const id = String(r[spec.idKey])
                const status = String(r.status)
                return (
                  <tr key={id}>
                    <td>{String(r.doc_no ?? '— draft —')}</td>
                    <td>{asDate(r.doc_date)}</td>
                    <td>{String(r.lines ?? 0)}</td>
                    <td>
                      <span className={`badge badge-${status.toLowerCase()}`}>
                        {status}
                      </span>
                    </td>
                    {valuesVisible && (
                      <td className="num">{num(r.total_value, 2)}</td>
                    )}
                    <td className="row-actions">
                      {/* Only a Draft offers actions. An authorised document
                          is in the ledger and a cancelled one is history —
                          neither has anything to do here, so neither shows a
                          button that would only refuse. */}
                      {status === 'Draft' && (
                        <>
                          {rights.can('authorise') && (
                            <button className="link" disabled={busy === id}
                                    onClick={() => void act(r, 'authorise')}>
                              Authorise
                            </button>
                          )}
                          {rights.can('cancel') && (
                            <button className="link" disabled={busy === id}
                                    onClick={() => void act(r, 'cancel')}>
                              Cancel
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
